import { hasConsent, subscribeToConsent, ConsentCategories } from "../consent/index.js";
import { parseTouch } from "./utm.js";
import {
  readAttributionCookie,
  writeAttributionCookie,
  clearAttributionCookie,
} from "./cookie.js";
import { AttributionStatus, AttributionModel } from "./constants.js";

/**
 * The semantic attribution API — the single source of truth for browser-side
 * campaign attribution across all three Agapaé surfaces.
 *
 * Two reads, deliberately distinct, mirroring the consent foundation:
 *   - `getStoredAttribution()` answers "is there a persisted record?" (`null`
 *     when there is not).
 *   - `getAttribution()` answers "what may I report right now?" and ALWAYS
 *     returns a usable state, so a consumer can never read absence as a
 *     campaign.
 *
 * THE CONSENT RULE, which drives the whole design:
 *
 *   Marketing attribution is not strictly necessary storage and has no
 *   legitimate-interest path. So a campaign click is parsed into MEMORY the
 *   moment the visitor arrives — because ordinary in-app navigation destroys
 *   the query string within seconds — and persisted only if and when consent
 *   permits. A visitor who leaves without answering the banner leaves nothing
 *   behind. A visitor who refuses has any existing record erased.
 *
 * Holding a value in a JavaScript variable for the life of a page is not
 * storage on the visitor's device; writing the cookie is. That distinction is
 * the entire reason this module has a pending buffer and a consent subscription.
 *
 * Phase 7.1 ships capture and persistence only. Nothing is sent anywhere, no
 * couple is associated, and no analytics event is enriched — see
 * docs/analytics/attribution-foundation.md.
 */

/**
 * A campaign touch captured this page load that consent does not yet permit
 * storing. Held in memory only; lost when the page unloads.
 *
 * Its VALUES are deliberately never exposed through the public API. Consent has
 * not been given, so nothing may consume them — making them unreachable is a
 * stronger guarantee than asking callers not to look.
 *
 * @type {import("./types.js").AttributionTouch | null}
 */
let pendingTouch = null;

/**
 * Once-per-page-load capture guard. Module-scoped so it survives component
 * mount/unmount — the same discipline as the section-view guard in the
 * analytics foundation. Without it, a remount would re-record the same arrival
 * and inflate `touchCount`.
 *
 * @type {boolean}
 */
let capturedThisLoad = false;

/**
 * Attribution-change subscribers. Module-scoped, per vendored copy — each
 * surface is a separate bundle on a separate origin, so a listener registered on
 * one is never expected to hear a write made on another. Cross-surface
 * propagation happens through the shared `.agapae.gr` cookie on the next page
 * load, not through this set.
 *
 * @type {Set<import("./types.js").AttributionListener>}
 */
const listeners = new Set();

/**
 * Unsubscribe handle for the consent subscription, or `null` when this module
 * has not been initialized. Doubles as the "already initialized" flag.
 *
 * @type {(() => void) | null}
 */
let unsubscribeFromConsent = null;

/**
 * Whether the visitor has permitted marketing storage.
 *
 * Attribution is campaign data, so it rides the MARKETING category rather than
 * ANALYTICS. Deny-by-default comes free: `hasConsent` returns `false` for an
 * unanswered visitor, a malformed record, and an unknown category alike.
 *
 * @returns {boolean}
 */
function mayStore() {
  return hasConsent(ConsentCategories.MARKETING);
}

/**
 * Whether this environment can persist anything at all. Distinguishes a genuine
 * `direct` visitor from an environment where capture could never have worked —
 * two very different rows in an acquisition report.
 *
 * @returns {boolean}
 */
function storageAvailable() {
  if (typeof document === "undefined" || typeof window === "undefined") return false;
  if (typeof navigator !== "undefined" && navigator.cookieEnabled === false) return false;
  return true;
}

/**
 * The state of a visitor with nothing recorded.
 *
 * @param {"direct"|"unavailable"|"consent_denied"} status
 * @returns {import("./types.js").AttributionState}
 */
function emptyState(status) {
  return {
    status,
    firstTouch: null,
    lastTouch: null,
    touchCount: 0,
    model: AttributionModel.FIRST_TOUCH,
    truncated: false,
    pending: false,
  };
}

/**
 * The persisted record, or `null` when nothing is stored.
 *
 * Returns the decoded cookie as-is — a fresh object every call, so there is no
 * shared mutable state to defend against.
 *
 * @returns {{ firstTouch: import("./types.js").AttributionTouch | null,
 *             lastTouch: import("./types.js").AttributionTouch | null,
 *             touchCount: number, truncated: boolean } | null}
 */
export function getStoredAttribution() {
  return readAttributionCookie();
}

/**
 * Whether there is a stored, usable attribution record.
 *
 * A pending (unconsented) touch does NOT count. "Has attribution" must mean
 * "there is attribution I am allowed to use"; any other reading invites a
 * consumer to act on data the visitor never agreed to.
 *
 * @returns {boolean}
 */
export function hasAttribution() {
  return getStoredAttribution() !== null;
}

/**
 * The EFFECTIVE attribution state — always usable, never `null`.
 *
 * @returns {import("./types.js").AttributionState}
 */
export function getAttribution() {
  const stored = getStoredAttribution();
  if (stored) {
    return {
      status: AttributionStatus.CAPTURED,
      firstTouch: stored.firstTouch,
      // A single-touch record has a last touch: the same one.
      lastTouch: stored.lastTouch || stored.firstTouch,
      touchCount: stored.touchCount,
      model: AttributionModel.FIRST_TOUCH,
      truncated: stored.truncated,
      pending: false,
    };
  }

  if (pendingTouch) {
    // Held but not stored. Values withheld on purpose — see `pendingTouch`.
    return { ...emptyState(AttributionStatus.CONSENT_DENIED), pending: true };
  }

  if (!storageAvailable()) return emptyState(AttributionStatus.UNAVAILABLE);
  return emptyState(AttributionStatus.DIRECT);
}

/**
 * Notify subscribers in isolation: one listener throwing must never prevent the
 * others from running, and must never surface to the code that recorded the
 * touch. Same discipline as the consent foundation and the backend analytics
 * provider fan-out — a broken observer cannot break the write.
 *
 * @returns {void}
 */
function notifyListeners() {
  if (listeners.size === 0) return;
  const state = getAttribution();
  for (const listener of listeners) {
    try {
      listener(state);
    } catch (err) {
      console.warn("[attribution][listener-failed]", err?.message);
    }
  }
}

/**
 * Subscribe to attribution changes: a capture, a flush after a late consent
 * grant, or a clear.
 *
 * Justified by the deferred-persist rule rather than by symmetry. A campaign
 * click can become storable at an arbitrary later moment — whenever the visitor
 * answers the banner — and a consumer has no way to poll for that. The touch
 * beacon a later phase adds is exactly this consumer.
 *
 * @param {import("./types.js").AttributionListener} listener
 * @returns {() => void} Unsubscribe. Safe to call more than once.
 */
export function subscribeToAttribution(listener) {
  if (typeof listener !== "function") return () => {};
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Merge a new touch into whatever is stored and persist the result.
 *
 * THE FIRST-TOUCH RULE: `firstTouch` is written once and never overwritten
 * while the record lives. `lastTouch` is replaced by every new touch, and
 * `touchCount` increments. When the record expires, the next touch starts a
 * fresh first touch — the correct semantic for a visitor re-acquired after 90
 * days of silence.
 *
 * @param {import("./types.js").AttributionTouch} touch
 * @returns {import("./types.js").AttributionState}
 */
function persistTouch(touch) {
  const stored = getStoredAttribution();

  const record = stored
    ? {
        firstTouch: stored.firstTouch,
        lastTouch: touch,
        touchCount: stored.touchCount + 1,
      }
    : { firstTouch: touch, lastTouch: touch, touchCount: 1 };

  writeAttributionCookie(record);
  pendingTouch = null;
  return getAttribution();
}

/**
 * Parse the current URL and record a touch if there is one.
 *
 * Runs at most once per page load. A second call is a no-op that simply reports
 * the current state, so it is safe from a remount, a re-render, or a second
 * entry point — pass `{ force: true }` only from tests or an explicit
 * re-capture.
 *
 * Behaviour by case:
 *   - no campaign signal   → nothing written; `direct` (or the stored record, if
 *                            an earlier visit left one).
 *   - signal + consent     → persisted; `captured`.
 *   - signal, no consent   → held in memory; `consent_denied` with
 *                            `pending: true`, to be flushed if consent arrives.
 *
 * @param {object} [options]
 * @param {boolean} [options.force=false]  Bypass the once-per-load guard.
 * @param {string}  [options.search]       Override `location.search` (tests).
 * @param {string}  [options.referrer]     Override `document.referrer` (tests).
 * @param {Date}    [options.now]          Injectable clock (tests).
 * @returns {import("./types.js").AttributionState}
 */
export function captureAttributionFromUrl(options = {}) {
  if (typeof window === "undefined") return emptyState(AttributionStatus.UNAVAILABLE);
  if (capturedThisLoad && !options.force) return getAttribution();
  capturedThisLoad = true;

  const touch = parseTouch({
    search: options.search !== undefined ? options.search : window.location.search,
    referrer:
      options.referrer !== undefined
        ? options.referrer
        : typeof document !== "undefined"
          ? document.referrer
          : "",
    pathname: window.location.pathname,
    hostname: window.location.hostname,
    now: options.now,
  });

  if (!touch) return getAttribution();

  if (mayStore()) {
    const state = persistTouch(touch);
    notifyListeners();
    return state;
  }

  pendingTouch = touch;
  notifyListeners();
  return getAttribution();
}

/**
 * Erase the stored record and drop any pending touch.
 *
 * Called on consent withdrawal as well as directly: continuing to hold
 * marketing data after consent is withdrawn is not lawful, and "stop writing
 * new records" is not the same as "stop holding the old one".
 *
 * @returns {import("./types.js").AttributionState}
 */
export function clearAttribution() {
  clearAttributionCookie();
  pendingTouch = null;
  const state = getAttribution();
  notifyListeners();
  return state;
}

/**
 * Wire attribution up: capture this arrival, then keep the record in step with
 * consent for the life of the page.
 *
 * Call as early as the entry point allows — before React renders, so the query
 * string is read before any client-side navigation can replace it.
 *
 * Idempotent: calling it again returns the existing subscription rather than
 * adding a second listener.
 *
 * @returns {() => void} Unsubscribe.
 */
export function initAttribution() {
  if (unsubscribeFromConsent) return unsubscribeFromConsent;

  captureAttributionFromUrl();

  const unsubscribe = subscribeToConsent(() => {
    if (mayStore()) {
      // Consent arrived after the click — flush what we held back.
      if (pendingTouch) {
        persistTouch(pendingTouch);
        notifyListeners();
      }
      return;
    }
    // Consent refused or withdrawn: hold nothing, keep nothing.
    if (pendingTouch || hasAttribution()) clearAttribution();
  });

  unsubscribeFromConsent = () => {
    unsubscribe();
    unsubscribeFromConsent = null;
  };

  return unsubscribeFromConsent;
}

/**
 * Return the module to its pre-`initAttribution()` state: drop the
 * subscription, the pending touch, and the once-per-load guard.
 *
 * Exists for tests, which need each case to start from a clean slate — all
 * three are module-scoped and would otherwise leak between them. Exported from
 * the module but NOT from the public barrel, so application code cannot reach
 * it by accident. Deliberately does NOT clear the cookie: a test that wants
 * that should say so.
 *
 * @returns {void}
 */
export function resetAttributionForTests() {
  if (unsubscribeFromConsent) unsubscribeFromConsent();
  unsubscribeFromConsent = null;
  pendingTouch = null;
  capturedThisLoad = false;
  listeners.clear();
}
